import { Injectable } from '@angular/core';
import { getClonedObject } from '@wms/core';
import { isEmpty, forEach, find, get, extend, includes } from 'lodash-es';
import { Subject } from 'rxjs';
import { runtimeEventManager } from '@wm/core';
import { EventManagerService, environment } from "@wms/app";
import { ProjectService, WmHttpService } from '@wms/services';
import { messages } from '../studio-messages/en';


@Injectable({
  providedIn: 'root'
})
export class I18nService {

  API_META = {
    getLocale: {
      url: environment.SERVICES_BASE_PATH + "services/projects/:projectId/i18n",
      method: 'GET',
      embedModeAPI: true
    },
    getPrefabMessages: {
      url: environment.SERVICES_BASE_PATH + "services/projects/:projectID/prefabs/:prefabName/i18n",
      method: 'GET'
    },
    putLocale: {
      url: environment.SERVICES_BASE_PATH + 'services/projects/:projectID/i18n',
      method: 'PUT'
    },
    getSystemMessages: {
      url: environment.SERVICES_BASE_PATH + 'services/projects/:projectID/i18n/system?locales=:locales',
      method: 'GET'
    },
    deleteLocale: {
      url: environment.SERVICES_BASE_PATH + 'services/projects/:projectID/i18n',
      method: 'DELETE'
    }
  };

  TARGET = "i18n";
  defaultLocale: any = 'en';
  supportedLocale: any = ['en'];
  rawLocaleResponse = []
  localeBundle: any = {};
  appLocale: any;
  locale: any;
  localeBundleSubject = new Subject();
  appDefaults: any = {};

  constructor(private wmHttpService: WmHttpService, private projectService: ProjectService,private eventManager: EventManagerService) {
    this.wmHttpService.registerApiMeta(this.TARGET, this.API_META);
    runtimeEventManager.setService('i18Services', this);
    this.locale = this.getMessages();
  }

  // getter method for app supported locale
  getAppSupportedLocale() {
    return this.supportedLocale.slice(0); // clone and send
  }

  // getter method for app default locale
  getAppDefaultLocale() {
    return this.defaultLocale;
  }

  getAppLocaleBundle(locale: any) {
    let bundle;
    if (!locale) {
      return;
    }
    bundle = this.localeBundle[locale];
    if (!bundle) {
      return {};
    }
    bundle.systemMessages = bundle.systemMessages || getClonedObject(this.localeBundle.en.systemMessages);
    return bundle;
  }

  // load the application messages bundle
  loadAppLocaleBundle(localePackage: any) {
    if (isEmpty(localePackage)) {
      return;
    }
    this.localeBundle = localePackage;
    let systemMessages = getClonedObject(get(localePackage[this.defaultLocale], 'systemMessages')) || {},
      userPrefabMessages = getClonedObject(get(localePackage[this.defaultLocale], 'userPrefabMessages')) || {},
      userMessages = get(localePackage[this.defaultLocale], 'userMessages');
    this.updateAppLocale(extend(systemMessages, userMessages, userPrefabMessages));
  }

  // update $rs.appLocale
  updateAppLocale(content: any) {
    this.appLocale = content;
  }

  getAppLocale() {
    return this.appLocale;
  }

  setAppDefaultFormats(formats: any) {
    this.appDefaults ={
      'dateFormat': formats.date,
      'timeFormat': formats.time,
      'dateTimeFormat': formats.date + ' ' + formats.time,
      'currencyCode': formats.currency
    };
    runtimeEventManager.emit('wms:set-app-defaults');
  }

  prepareLocaleBundles(bundledPackage: any) {
    let locales: any = {},
      supportedLocale: any = [];
      this.rawLocaleResponse = bundledPackage;
    forEach(bundledPackage, (localeObj: any) => {
      let localeKey = localeObj.locale;
      supportedLocale.push(localeKey);
      locales[localeKey] = {};
      if (localeObj.systemMessagesFallbackLocale) {
        let fallbackLocale = find(bundledPackage, { locale: localeObj.systemMessagesFallbackLocale });
        locales[localeKey].systemMessages = get(fallbackLocale, 'system.messages');
      } else {
        locales[localeKey].systemMessages = localeObj.system.messages;
      }
      locales[localeKey].userMessages = localeObj.user ? localeObj.user.messages : {};
      locales[localeKey].userPrefabMessages = localeObj.user.prefabMessages ? localeObj.user.prefabMessages : {};
      locales[localeKey].userFormats = localeObj.user ? localeObj.user.formats : {};
      locales[localeKey].systemFormats = localeObj.system ? localeObj.system.formats : {};
      locales[localeKey].userFiles = localeObj.user ? localeObj.user.files : {};
      locales[localeKey].systemFiles = localeObj.system ? localeObj.system.formats : {};
    });
    this.supportedLocale = supportedLocale;
    if (!isEmpty(locales)) {
      this.setAppDefaultLocaleFromPreferences();
      this.loadAppLocaleBundle(locales);
      // $rs.$emit('wms:update-and-save-supportedLocale-variable', _supportedLocale);
      this.eventManager.emitUpdateSupportedVar(supportedLocale);
    }
    this.localeBundleSubject.next(true);
  }

  setAppDefaultLocaleFromPreferences(forceLoadLocale?: any) {
    let dl = this.projectService.getDetails().properties.defaultLanguage || 'en';
    /* force reload locale and set the default locale when project settings are changed*/
    if (dl === this.defaultLocale && !forceLoadLocale) {
      let localeObj = find(this.rawLocaleResponse, { locale: dl }),
        formats: any = get(localeObj, 'user.formats');

      formats = isEmpty(formats) ? get(localeObj, 'system.formats') : formats;
      if (formats) {
        this.setAppDefaultFormats(formats);
      }
      return;
    }

    this.setAppDefaultLocale(dl);
  }

  // setter method for app default locale
  setAppDefaultLocale(locale: any) {
    this.addAppSupportedLocale(locale);
    this.defaultLocale = locale;
  }

  //sets the app supported locale
  setAppSupportedLocale(supportedLocale: any) {
    this.supportedLocale = supportedLocale;
  }

  addAppSupportedLocale(locale: any) {
    // TO-DO add it to below condition  && CONSTANTS.isStudioMode
    if (!includes(this.supportedLocale, locale)) {
      this.supportedLocale.push(locale);
      let __supportedLocale = getClonedObject(this.supportedLocale);
      //get locale bundle and add new locale to the bundle and save them
      //save supported locale bundle related appVariable
      (this.getLocaleBundle() as Promise<any>).then(() => {
        let defaultLanguage = this.projectService.getDetails().properties.defaultLanguage,
          newBundle: any = getClonedObject(this.rawLocaleResponse),
          fallbackLocale: any = find(this.rawLocaleResponse, { locale: 'en' });
        (this.getSystemMessages(defaultLanguage) as Promise<any>).then((localeBundles: any) => {
          forEach(localeBundles, (localeObj: any) => {
            newBundle.push({
              'locale': defaultLanguage,
              'system': {
                'messages': fallbackLocale.system.messages,
                'formats': localeObj.system ? localeObj.system.formats : {}
              },
              'user': {
                'messages': fallbackLocale.user.messages,
                'formats': localeObj.system ? localeObj.system.formats : {}
              }
            });
          });

          let formats = get(find(newBundle, { locale: locale }), 'user.formats');
          if (formats) {
            this.setAppDefaultFormats(formats);
          }
          (this.saveAppLocaleBundle(newBundle) as Promise<any>).then(() => {
            // $rs.$emit('wms:update-and-save-supportedLocale-variable', __supportedLocale);
            this.eventManager.emitUpdateSupportedVar(__supportedLocale);
            this.getLocaleBundle();
          });
        });

      });
    } else {
      let formats = get(find(this.rawLocaleResponse, { locale: locale }), 'user.formats');
      if (formats) {
        this.setAppDefaultFormats(formats);
      }
    }
  }

  // gets the system messages for the locales
  getSystemMessages(locales: any) {
    return (this.wmHttpService.execute({
      target: 'i18n',
      action: 'getSystemMessages',
      urlParams: {
        projectID: this.projectService.getProjectId(),
        locales: locales
      },
      spinnerInfo: {
        showSpinner: false
      }
    }) as Promise<any>);
  }

  // get the prefab messages of the selected prefab.
  getPrefabMessages(prefabName: string) {
    return (this.wmHttpService.execute({
      target: 'i18n',
      action: 'getPrefabMessages',
      urlParams: {
        projectID: this.projectService.getProjectId(),
        prefabName: prefabName
      },
      spinnerInfo: {
        showSpinner: false
      }
    }) as Promise<any>);
  }

  //fetches the latest locale bundle
  getLocaleBundle() {
    return new Promise((res, rej) => {
      (this.wmHttpService.execute({
        target: 'i18n',
        action: 'getLocale',
        pathParams: {
          projectId: this.projectService.getProjectId()
        },
        spinnerInfo: {
          showSpinner: false
        }
      }) as Promise<any>).then((response: any) => {
        res(this.prepareLocaleBundles(response));
      });
    });
  }

  // saves the app message bundle
  saveAppLocaleBundle(bundle: any ,ShowSpinner:boolean =false): Promise<any> {
    return (this.wmHttpService.execute({
      target: 'i18n',
      action: 'putLocale',
      data: bundle,
      urlParams: {
        projectID: this.projectService.getProjectId()
      },
      toasterInfo: {
        'success': { title: this.locale.MESSAGE_APP_MESSAGES_SAVE_SUCCESS_TITLE, description: this.locale.MESSAGE_APP_MESSAGES_SAVE_SUCCESS_DESC },
        'error': { title: this.locale.MESSAGE_APP_MESSAGES_SAVE_ERROR_TITLE, description: this.locale.MESSAGE_APP_MESSAGES_SAVE_ERROR_DESC }
      },
      spinnerInfo: {
        showSpinner: ShowSpinner
      }
    }) as Promise<any>);
  }

  // deletes the locale from the fileservice
  deleteLocale(locales: any) {
    return (this.wmHttpService.execute({
      target: 'i18n',
      action: 'deleteLocale',
      data: locales,
      urlParams: {
        projectID: this.projectService.getProjectId()
      }
    }));
  }

  getMessages() {
    return messages;
  }
}
